import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { FiCalendar, FiCopy } from 'react-icons/fi';
import EditGodMemberForm from '../components/EditGodMemberForm';

/** Checklist key, label, and copy value from member (memberDetails). Used when selectedDate !== 'hold'. */
const CHECKLIST_CONFIG = [
    { key: 'reminderSent', label: 'Reminder Send', getCopyValue: (m) => 'Search ' + (m.memberDetails?.webID ?? '') },
    { key: 'setWebpagePrivate', label: 'Set Webpage Private', getCopyValue: (m) => m.memberDetails?.website ?? '' },
    { key: 'muteInGroup', label: 'Mute in Group', getCopyValue: (m) => { const arr = m.memberDetails?.whatsAppNumbers; const first = Array.isArray(arr) && arr[0] ? arr[0] : null; return (first && (typeof first === 'string' ? first : first.whatsAppNumber)) ?? ''; } },
    { key: 'deleteFromGroup', label: 'Delete from Group', getCopyValue: (m) => { const arr = m.memberDetails?.whatsAppNumbers; const first = Array.isArray(arr) && arr[0] ? arr[0] : null; return (first && (typeof first === 'string' ? first : first.whatsAppNumber)) ?? ''; } },
    { key: 'deleteFromSBF', label: 'Delete from SBF', getCopyValue: (m) => { const s = m.memberDetails?.sbfID; return Array.isArray(s) ? s.join(', ') : (s ?? ''); } },
    { key: 'hideReviewCorner', label: 'Hide Reviews', getCopyValue: (m) => '#C' + (m.memberDetails?.webID ?? '') },
    { key: 'updateGoogleSheet', label: 'Update Google Sheet', getCopyValue: (m) => m.memberDetails?.webID ?? '' },
];

const ExpiringMemberPage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expiringMembers, setExpiringMembers] = useState([]);
    const [uniqueDates, setUniqueDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [groupTotals, setGroupTotals] = useState({});
    const [copiedBadge, setCopiedBadge] = useState(null);
    const [copiedChecklistCell, setCopiedChecklistCell] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedGodMemberForEdit, setSelectedGodMemberForEdit] = useState(null);

    // Function to format date consistently
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    // Function to get latest payment info
    const getLatestPaymentInfo = useCallback((member, isGroupMember = false) => {
        if (isGroupMember) return null;
        
        if (selectedDate === 'hold' && member.payments) {
            const sortedPayments = [...member.payments].sort((a, b) => 
                new Date(b.paymentDate) - new Date(a.paymentDate)
            );
            const latest = sortedPayments[0];
            return latest ? {
                date: latest.paymentDate,
                amount: latest.paymentAmount
            } : null;
        }
        return member.latestPayment ? {
            date: member.latestPayment.paymentDate,
            amount: member.latestPayment.paymentAmount
        } : null;
    }, [selectedDate]);

    // Function to format full name
    const formatFullName = useCallback((member) => {
        if (selectedDate === 'hold') {
            const lastOldName = member.oldNames?.length > 0 ? member.oldNames[member.oldNames.length - 1] : "N/A";
            const godName = member.godName || "N/A";
            const webID = member.webID || "N/A";
            if (lastOldName !== "N/A") {
                return `${member.godID} - ${lastOldName}(${godName}) - ${webID}`;
            } else {
                return `${member.godID} - ${godName} - ${webID}`;
            }
        } else {
            const m = member.memberDetails;
            const lastOldName = m?.oldNames?.length > 0 ? m.oldNames[m.oldNames.length - 1] : "N/A";
            const godName = m?.godName || "N/A";
            const webID = m?.webID || "N/A";
            if (lastOldName !== "N/A") {
                return `${m?.godID} - ${lastOldName}(${godName}) - ${webID}`;
            } else {
                return `${m?.godID} - ${godName} - ${webID}`;
            }
        }
    }, [selectedDate]);

    /** Format full name from details object (e.g. handler: { godID, godName, webID, oldNames }). */
    const formatFullNameFromDetails = useCallback((details) => {
        if (!details) return 'N/A';
        const lastOldName = details.oldNames?.length > 0 ? details.oldNames[details.oldNames.length - 1] : 'N/A';
        const godName = details.godName || 'N/A';
        const webID = details.webID || 'N/A';
        const godID = details.godID || 'N/A';
        if (lastOldName !== 'N/A') return `${godID} - ${lastOldName}(${godName}) - ${webID}`;
        return `${godID} - ${godName} - ${webID}`;
    }, []);

    /** Format date as "1Jan2026" for copy text. */
    const formatDateCopy = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        const day = d.getDate();
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${day}${months[d.getMonth()]}${d.getFullYear()}`;
    };

    const showCopiedBadge = useCallback((section) => {
        setCopiedBadge(section);
        const t = setTimeout(() => setCopiedBadge(null), 2000);
        return () => clearTimeout(t);
    }, []);

    const handleCopyChecklistValue = useCallback((member, key) => {
        const config = CHECKLIST_CONFIG.find(c => c.key === key);
        if (!config) return;
        const text = config.getCopyValue(member);
        navigator.clipboard.writeText(text).then(() => {
            setCopiedChecklistCell(`${member.memberId}_${key}`);
            setTimeout(() => setCopiedChecklistCell(null), 2000);
        });
    }, []);

    const copyIndividualToClipboard = useCallback(() => {
        const list = selectedMembers.individual || [];
        if (!list.length) return;
        const dueLabel = selectedDate === 'hold' ? 'Individual (HOLD)' : `Individual due on ${formatDateCopy(selectedDate)}`;
        const lines = [dueLabel];
        list.forEach((member, i) => {
            const fullName = formatFullName(member);
            const username = `@${member.username}` ?? '';
            const num = i + 1;
            lines.push(`\n${num}. ${fullName} - ${username}`);
            if (member.lastPostDate) lines.push(`Last Post Date: ${member.lastPostDate}`);
            const paymentInfo = getLatestPaymentInfo(member);
            if (paymentInfo) {
                const amt = paymentInfo.amount != null ? `$${Number(paymentInfo.amount).toFixed(2)}` : '';
                const datePart = paymentInfo.date ? formatDateCopy(paymentInfo.date) : '';
                lines.push(`Last Payment: ${[amt, datePart].filter(Boolean).join(' ')}`);
            }
        });
        const text = lines.join('\n');
        navigator.clipboard.writeText(text).then(() => showCopiedBadge('individual'));
    }, [selectedMembers.individual, selectedDate, formatFullName, getLatestPaymentInfo, showCopiedBadge, formatDateCopy]);

    const copyGroupToClipboard = useCallback((groupName, group) => {
        const renewalDate = group.renewalDate ? formatDateCopy(group.renewalDate) : '';
        const handler = group.members[0]?.latestGodGroup?.groupMembers?.handler;
        const handlerFullName = handler ? formatFullNameFromDetails(handler) : '';
        const groupId = group.members[0]?.latestGodGroup?._id;
        const totalAmount = groupId ? (groupTotals[groupId] ?? 0) : 0;
        const count = group.members?.length ?? 0;
        const lines = [
            `${groupName} due on ${renewalDate}`,
            '1. Handler: ' + handlerFullName,
            'Last Payment: ' + (totalAmount ? `$${Number(totalAmount).toFixed(2)}` : ''),
            `Participant (${count}):`
        ];
        (group.members || []).forEach((member, i) => {
            const fullName = formatFullName(member);
            const username = `@${member.username}` ?? '';
            const lastPost = member.lastPostDate ? ` (Last Post Date: ${member.lastPostDate})` : '';
            lines.push(`(${i + 1}). ${fullName} - ${username}${lastPost}`);
        });
        const text = lines.join('\n');
        navigator.clipboard.writeText(text).then(() => showCopiedBadge(groupName));
    }, [groupTotals, formatFullName, formatFullNameFromDetails, showCopiedBadge]);

    // Function to calculate total amount
    const calculateTotalAmount = (members) => {
        return members.reduce((total, member) => {
            const latestPayment = member.latestPayment || 
                (member.payments?.sort((a, b) => 
                    new Date(b.paymentDate) - new Date(a.paymentDate)
                )[0]);
            return total + (latestPayment?.paymentAmount || 0);
        }, 0);
    };

    // Toggle row expansion
    const toggleRow = (memberId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(memberId)) {
                newSet.delete(memberId);
            } else {
                newSet.add(memberId);
            }
            return newSet;
        });
    };

    // Fetch expiring members data
    const fetchExpiringMembers = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/expiring-members${selectedDate === 'hold' ? '?status=hold' : ''}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                console.log('Received expiring members:', response.data.data);
                setExpiringMembers(response.data.data);
                setGroupTotals(response.data.groupTotals || {});
                
                // Always update date buttons
                const dates = [...new Set(response.data.allMembers
                    .map(member => member.memberDetails?.renewalDate)
                    .filter(date => date)
                )].sort();
                setUniqueDates(dates);

                if (selectedDate === 'hold') {
                    setSelectedMembers({
                        individual: response.data.data
                    });
                } else if (selectedDate) {
                    handleDateSelect(selectedDate);
                }
            }
        } catch (err) {
            setError(`Failed to fetch expiring members: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpiringMembers();
    }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle date selection
    const handleDateSelect = (date) => {
        setSelectedDate(date);
        
        if (date === 'hold') {
            console.log('HOLD members:', expiringMembers);
            // For HOLD members, just put them all in individual array
            setSelectedMembers({
                individual: expiringMembers
            });
            return;
        }

        const membersForDate = expiringMembers.filter(
            member => member.memberDetails?.renewalDate === date
        );
        
        console.log('Members for date:', membersForDate);
        
        // Group members by their God Group if they have matching renewal dates
        const groupedMembers = membersForDate.reduce((acc, member) => {
            const groupKey = member.latestGodGroup?.groupName;
            if (groupKey && member.latestGodGroup.renewalDate === member.memberDetails.renewalDate) {
                if (!acc[groupKey]) {
                    acc[groupKey] = {
                        groupName: groupKey,
                        renewalDate: member.latestGodGroup.renewalDate,
                        members: [],
                        totalAmount: 0
                    };
                }
                acc[groupKey].members.push(member);
                // Add payment amount to total if available
                if (member.latestPayment?.paymentAmount) {
                    acc[groupKey].totalAmount += member.latestPayment.paymentAmount;
                }
            } else {
                if (!acc.individual) {
                    acc.individual = [];
                }
                acc.individual.push(member);
            }
            return acc;
        }, {});

        console.log('Grouped members:', groupedMembers);
        setSelectedMembers(groupedMembers);
    };

    // Handle checklist update with optimistic updates
    const updateChecklist = async (memberId, field) => {
        // Optimistic update: immediately update the UI
        setExpiringMembers(prevMembers => 
            prevMembers.map(member => {
                if (member.memberId === memberId) {
                    return {
                        ...member,
                        checklist: {
                            ...member.checklist,
                            [field]: !member.checklist?.[field] // Toggle the checkbox state
                        }
                    };
                }
                return member;
            })
        );

        // Also update selectedMembers if it contains this member
        setSelectedMembers(prevSelected => {
            const newSelected = { ...prevSelected };
            
            // Update individual members
            if (newSelected.individual) {
                newSelected.individual = newSelected.individual.map(member => {
                    if (member.memberId === memberId) {
                        return {
                            ...member,
                            checklist: {
                                ...member.checklist,
                                [field]: !member.checklist?.[field]
                            }
                        };
                    }
                    return member;
                });
            }
            
            // Update grouped members
            Object.keys(newSelected).forEach(key => {
                if (key !== 'individual' && newSelected[key].members) {
                    newSelected[key].members = newSelected[key].members.map(member => {
                        if (member.memberId === memberId) {
                            return {
                                ...member,
                                checklist: {
                                    ...member.checklist,
                                    [field]: !member.checklist?.[field]
                                }
                            };
                        }
                        return member;
                    });
                }
            });
            
            return newSelected;
        });

        // Make API call in background
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${process.env.REACT_APP_API_URL}/api/expiring-members/${memberId}/checklist`,
                { [field]: true },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            // If API call succeeds, no need to do anything (optimistic update was correct)
            // No popup needed for successful updates
        } catch (err) {
            console.error('Failed to update checklist:', err);
            
            // Revert optimistic update on error
            setExpiringMembers(prevMembers => 
                prevMembers.map(member => {
                    if (member.memberId === memberId) {
                        return {
                            ...member,
                            checklist: {
                                ...member.checklist,
                                [field]: !member.checklist?.[field] // Revert the toggle
                            }
                        };
                    }
                    return member;
                })
            );

            // Also revert selectedMembers
            setSelectedMembers(prevSelected => {
                const newSelected = { ...prevSelected };
                
                if (newSelected.individual) {
                    newSelected.individual = newSelected.individual.map(member => {
                        if (member.memberId === memberId) {
                            return {
                                ...member,
                                checklist: {
                                    ...member.checklist,
                                    [field]: !member.checklist?.[field]
                                }
                            };
                        }
                        return member;
                    });
                }
                
                Object.keys(newSelected).forEach(key => {
                    if (key !== 'individual' && newSelected[key].members) {
                        newSelected[key].members = newSelected[key].members.map(member => {
                            if (member.memberId === memberId) {
                                return {
                                    ...member,
                                    checklist: {
                                        ...member.checklist,
                                        [field]: !member.checklist?.[field]
                                    }
                                };
                            }
                            return member;
                        });
                    }
                });
                
                return newSelected;
            });

            const errorMessage = `Failed to update checklist: ${err.response?.data?.error || err.message}. Status: ${err.response?.status}`;
            setError(errorMessage);
            alert(errorMessage);
        }
    };

    // Handle status change with optimistic updates
    const handleStatusChange = async (memberId, newStatus) => {
        const member = expiringMembers.find(m => m.memberId === memberId);
        
        if (newStatus === 'cancelled') {
            // Check if all checklist items are completed
            const checklist = member?.checklist || {};
            const allCompleted = Object.values(checklist).every(value => value === true);
            if (!allCompleted) {
                setError('Please complete all checklist items before cancelling.');
                return;
            }
        }

        // Optimistic update: immediately update the UI
        setExpiringMembers(prevMembers => 
            prevMembers.filter(m => m.memberId !== memberId)
        );

        // Also update selectedMembers
        setSelectedMembers(prevSelected => {
            const newSelected = { ...prevSelected };
            
            // Remove from individual members
            if (newSelected.individual) {
                newSelected.individual = newSelected.individual.filter(m => m.memberId !== memberId);
            }
            
            // Remove from grouped members
            Object.keys(newSelected).forEach(key => {
                if (key !== 'individual' && newSelected[key].members) {
                    newSelected[key].members = newSelected[key].members.filter(m => m.memberId !== memberId);
                }
            });
            
            return newSelected;
        });

        // Make API call in background
        try {
            const token = localStorage.getItem('token');
            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/api/expiring-members/${memberId}/status`,
                { status: newStatus },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );

            if (response.data.success) {
                // No need to refresh - optimistic update was correct
                // No popup needed for successful updates
            }
        } catch (err) {
            console.error('Failed to update status:', err);
            
            // Revert optimistic update on error by refetching data
            fetchExpiringMembers();
            const errorMessage = `Failed to update status: ${err.response?.data?.error || err.message}`;
            setError(errorMessage);
            alert(errorMessage);
        }
    };

    // Get color for row based on index
    const getRowColor = (index, isGroup = false) => {
        const colors = [
            'bg-blue-50',
            'bg-green-50',
            'bg-yellow-50',
            'bg-purple-50',
            'bg-pink-50',
            'bg-indigo-50',
            'bg-red-50',
            'bg-orange-50'
        ];
        return isGroup ? colors[(index + 3) % colors.length] : colors[index % colors.length];
    };

    // Table header component for consistency
    const TableHeader = () => (
        <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                FULL NAME
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                REMARKS
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                LAST PAYMENT (DATE & AMOUNT)
            </th>
            {selectedDate !== 'hold' && (
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACTIONS
                </th>
            )}
        </tr>
    );

    return (
        <Layout>
            <div className="px-4 py-6">
                <div className="flex items-center mb-6">
                    <FiCalendar className="h-6 w-6 text-blue-500 mr-2" />
                    <h1 className="text-2xl font-bold">Expiring Members</h1>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {/* Date buttons */}
                <div className="flex flex-wrap gap-2 mb-6">
                    <button
                        onClick={() => handleDateSelect('hold')}
                        className={`px-4 py-2 rounded-md ${
                            selectedDate === 'hold' 
                            ? 'bg-yellow-500 text-white' 
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                    >
                        HOLD
                    </button>
                    {uniqueDates.slice(0, 10).map((date, index) => (
                        <button
                            key={date}
                            onClick={() => handleDateSelect(date)}
                            className={`px-4 py-2 rounded-md ${
                                selectedDate === date 
                                ? 'bg-blue-500 text-white' 
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                        >
                            {formatDate(date)}
                        </button>
                    ))}
                </div>

                {/* Member list */}
                {loading ? (
                    <div className="text-center py-4">Loading...</div>
                ) : selectedDate ? (
                    <div className="space-y-6">
                        {/* Individual members */}
                        {selectedMembers.individual && selectedMembers.individual.length > 0 && (
                            <div className="overflow-hidden rounded-lg border border-gray-200">
                                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                                    <h3 className="text-lg font-medium">INDIVIDUAL</h3>
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); copyIndividualToClipboard(); }}
                                        className="p-1.5 rounded hover:bg-gray-200 text-gray-600 relative"
                                        title="Copy"
                                    >
                                        <FiCopy className="w-4 h-4" />
                                        {copiedBadge === 'individual' && (
                                            <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-0.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap">Copied</span>
                                        )}
                                    </button>
                                </div>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <TableHeader />
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {selectedMembers.individual.map((member, index) => (
                                            <>
                                                <tr key={member.memberId} 
                                                    className={`${getRowColor(index)} cursor-pointer`}
                                                    onClick={() => toggleRow(member.memberId)}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {formatFullName(member)}
                                                        <span className="text-gray-500 font-normal ml-1">(Reviews: {member.reviewCount ?? 0})</span>
                                                        {(selectedDate === 'hold' ? member.autobot : member.memberDetails?.autobot) && (
                                                            <span className="text-red-600 font-semibold ml-1">‼️‼️‼️ Autobot ON</span>
                                                        )}
                                                        <span className="text-blue-600 font-normal ml-1">Last Post Date: {member.lastPostDate ?? 'N/A'}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">
                                                        {selectedDate === 'hold' ? member.remarks : member.memberDetails?.remarks || 'N/A'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {(() => {
                                                            const paymentInfo = getLatestPaymentInfo(member);
                                                            if (!paymentInfo) return 'N/A';
                                                            return (
                                                                <>
                                                                    {formatDate(paymentInfo.date)}
                                                                    {paymentInfo.amount && (
                                                                        <span className="ml-2 text-green-600">
                                                                            ${paymentInfo.amount.toFixed(2)}
                                                                        </span>
                                                                    )}
                                                                </>
                                                            );
                                                        })()}
                                                    </td>
                                                    {selectedDate !== 'hold' && (
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                            <div className="space-x-2">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleStatusChange(member.memberId, 'cancelled');
                                                                    }}
                                                                    className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleStatusChange(member.memberId, 'renewed');
                                                                    }}
                                                                    className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200"
                                                                >
                                                                    Renew
                                                                </button>
                                                                <button
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            const token = localStorage.getItem('token');
                                                                            const res = await axios.get(
                                                                                `${process.env.REACT_APP_API_URL}/api/godmembers/${member.memberId}?view=edit`,
                                                                                { headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' } }
                                                                            );
                                                                            if (res.data?.success && res.data?.data) {
                                                                                setSelectedGodMemberForEdit(res.data.data);
                                                                                setShowEditModal(true);
                                                                            }
                                                                        } catch (err) {
                                                                            setError(err.response?.data?.error || err.message);
                                                                        }
                                                                    }}
                                                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                                                                >
                                                                    Edit
                                                                </button>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                                {selectedDate !== 'hold' && expandedRows.has(member.memberId) && (
                                                    <tr className={getRowColor(index)}>
                                                        <td colSpan="4" className="px-6 py-4">
                                                            <div className="space-y-2">
                                                                {CHECKLIST_CONFIG.map(({ key, label, getCopyValue }) => {
                                                                    const value = member.checklist?.[key] ?? false;
                                                                    const cellId = `${member.memberId}_${key}`;
                                                                    const copyValue = getCopyValue(member);
                                                                    return (
                                                                        <div key={key} className="flex items-center gap-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={value}
                                                                                onChange={(e) => {
                                                                                    e.stopPropagation();
                                                                                    updateChecklist(member.memberId, key);
                                                                                }}
                                                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                            />
                                                                            <span className="text-sm text-gray-600">{label}</span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => { e.stopPropagation(); handleCopyChecklistValue(member, key); }}
                                                                                className="p-1 rounded hover:bg-gray-200 text-gray-600 relative"
                                                                                title={`Copy: ${copyValue}`}
                                                                            >
                                                                                <FiCopy className="w-4 h-4" />
                                                                                {copiedChecklistCell === cellId && (
                                                                                    <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-0.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap">Copied</span>
                                                                                )}
                                                                            </button>
                                                                            <span className="text-sm text-gray-500">({copyValue || '—'})</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Grouped members */}
                        {Object.entries(selectedMembers)
                            .filter(([key]) => key !== 'individual')
                            .map(([groupName, group], groupIndex) => (
                                <div key={groupName} className={`rounded-lg border border-gray-200 overflow-hidden ${getRowColor(groupIndex, true)}`}>
                                    <div className="px-6 py-4 border-b border-gray-200">
                                        <div className="flex justify-between items-center">
                                            <h3 className="text-lg font-medium flex items-center gap-2">
                                                Group: {groupName}
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); copyGroupToClipboard(groupName, group); }}
                                                    className="p-1.5 rounded hover:bg-gray-200 text-gray-600 relative"
                                                    title="Copy"
                                                >
                                                    <FiCopy className="w-4 h-4" />
                                                    {copiedBadge === groupName && (
                                                        <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-0.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap">Copied</span>
                                                    )}
                                                </button>
                                            </h3>
                                            <div className="text-sm">
                                                <span className="font-medium">Total Amount:</span>{' '}
                                                ${(groupTotals[group.members[0]?.latestGodGroup?._id] || 0).toFixed(2)}
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            Renewal Date: {formatDate(group.renewalDate)}
                                        </div>
                                    </div>
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-white bg-opacity-50">
                                            <TableHeader />
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {group.members.map((member) => (
                                                <>
                                                    <tr key={member.memberId} 
                                                        className="cursor-pointer"
                                                        onClick={() => toggleRow(member.memberId)}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {formatFullName(member)}
                                                            <span className="text-gray-500 font-normal ml-1">(Reviews: {member.reviewCount ?? 0})</span>
                                                            {(selectedDate === 'hold' ? member.autobot : member.memberDetails?.autobot) && (
                                                                <span className="text-red-600 font-semibold ml-1">‼️‼️‼️ Autobot ON</span>
                                                            )}
                                                            <span className="text-blue-600 font-normal ml-1">Last Post Date: {member.lastPostDate ?? 'N/A'}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500">
                                                            {selectedDate === 'hold' ? member.remarks : member.memberDetails?.remarks || 'N/A'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            N/A
                                                        </td>
                                                        {selectedDate !== 'hold' && (
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                <div className="space-x-2">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleStatusChange(member.memberId, 'cancelled');
                                                                        }}
                                                                        className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleStatusChange(member.memberId, 'renewed');
                                                                        }}
                                                                        className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200"
                                                                    >
                                                                        Renew
                                                                    </button>
                                                                    <button
                                                                        onClick={async (e) => {
                                                                            e.stopPropagation();
                                                                            try {
                                                                                const token = localStorage.getItem('token');
                                                                                const res = await axios.get(
                                                                                    `${process.env.REACT_APP_API_URL}/api/godmembers/${member.memberId}?view=edit`,
                                                                                    { headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' } }
                                                                                );
                                                                                if (res.data?.success && res.data?.data) {
                                                                                    setSelectedGodMemberForEdit(res.data.data);
                                                                                    setShowEditModal(true);
                                                                                }
                                                                            } catch (err) {
                                                                                setError(err.response?.data?.error || err.message);
                                                                            }
                                                                        }}
                                                                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                    {selectedDate !== 'hold' && expandedRows.has(member.memberId) && (
                                                        <tr>
                                                            <td colSpan="4" className="px-6 py-4">
                                                                <div className="space-y-2">
                                                                    {CHECKLIST_CONFIG.map(({ key, label, getCopyValue }) => {
                                                                        const value = member.checklist?.[key] ?? false;
                                                                        const cellId = `${member.memberId}_${key}`;
                                                                        const copyValue = getCopyValue(member);
                                                                        return (
                                                                            <div key={key} className="flex items-center gap-2">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={value}
                                                                                    onChange={(e) => {
                                                                                        e.stopPropagation();
                                                                                        updateChecklist(member.memberId, key);
                                                                                    }}
                                                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                                />
                                                                                <span className="text-sm text-gray-600">{label}</span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => { e.stopPropagation(); handleCopyChecklistValue(member, key); }}
                                                                                    className="p-1 rounded hover:bg-gray-200 text-gray-600 relative"
                                                                                    title={`Copy: ${copyValue}`}
                                                                                >
                                                                                    <FiCopy className="w-4 h-4" />
                                                                                    {copiedChecklistCell === cellId && (
                                                                                        <span className="absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-0.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap">Copied</span>
                                                                                    )}
                                                                                </button>
                                                                                <span className="text-sm text-gray-500">({copyValue || '—'})</span>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                    </div>
                ) : (
                    <div className="text-center py-4 text-gray-500">
                        Select a date or HOLD to view members
                    </div>
                )}
            </div>

            {showEditModal && selectedGodMemberForEdit && (
                <EditGodMemberForm
                    member={selectedGodMemberForEdit}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedGodMemberForEdit(null);
                        fetchExpiringMembers();
                    }}
                    onCancel={() => {
                        setShowEditModal(false);
                        setSelectedGodMemberForEdit(null);
                    }}
                />
            )}
        </Layout>
    );
};

export default ExpiringMemberPage; 